import React, { createContext, useContext, useMemo } from 'react';

// ---- Types ----

/**
 * CRM staff roles — must match JwtPayload.roles values from crm-api.
 */
export type CrmRole =
  | 'loan_officer'
  | 'marketing_staff'
  | 'compliance_officer'
  | 'admin';

export interface AuthUser {
  sub: string;
  email: string;
  tenantId: string;
  cuName: string;
  roles: CrmRole[];
  /** ISO 8601 expiration timestamp */
  exp: number;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  hasRole: (role: CrmRole) => boolean;
  hasAnyRole: (roles: CrmRole[]) => boolean;
}

// ---- Context ----

const AuthContext = createContext<AuthContextValue | null>(null);

// ---- JWT Parsing ----

/**
 * Parse a JWT from the 'crm_session' cookie.
 *
 * Reads document.cookie (HttpOnly cookies are NOT accessible; this assumes
 * the session cookie is NOT HttpOnly for dev — Phase 2 uses a token exchange
 * endpoint so the app only receives non-sensitive claims).
 *
 * In production: Entra ID OIDC flow sets a short-lived session cookie.
 * In dev: use DEV_AUTH_COOKIE env or a mock cookie injected by the dev server.
 */
function parseJwtFromCookie(): AuthUser | null {
  try {
    // Read 'crm_session' cookie
    const cookieToken = getCookieValue('crm_session');

    // Dev fallback: check for mock token in localStorage (set by dev auth mock)
    const token = cookieToken ?? localStorage.getItem('crm_dev_token');

    if (token === null) {
      return null;
    }

    const payload = decodeJwtPayload(token);
    if (payload === null) {
      return null;
    }

    // Validate expiration
    const nowSeconds = Math.floor(Date.now() / 1000);
    if (typeof payload['exp'] === 'number' && payload['exp'] < nowSeconds) {
      // Expired — clear dev storage
      localStorage.removeItem('crm_dev_token');
      return null;
    }

    return {
      sub: String(payload['sub'] ?? ''),
      email: String(payload['email'] ?? ''),
      tenantId: String(payload['tenantId'] ?? ''),
      cuName: String(payload['cuName'] ?? ''),
      roles: (payload['roles'] as CrmRole[]) ?? [],
      exp: typeof payload['exp'] === 'number' ? payload['exp'] : 0,
    };
  } catch {
    return null;
  }
}

function getCookieValue(name: string): string | null {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match !== undefined ? (match.split('=')[1] ?? null) : null;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }
  // Safe: we're only reading public claims from the payload (middle segment)
  // Signature validation happens on the server; client reads display claims only
  const payloadBase64 = parts[1];
  if (payloadBase64 === undefined) {
    return null;
  }

  const padded = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
  const jsonString = atob(padded);
  return JSON.parse(jsonString) as Record<string, unknown>;
}

// ---- Provider ----

interface AuthProviderProps {
  children: React.ReactNode;
  /** Override for testing — skips cookie parsing */
  mockUser?: AuthUser | null;
}

export function AuthProvider({ children, mockUser }: AuthProviderProps): React.ReactElement {
  // Parse user from cookie on mount (memoized — cookie doesn't change mid-session)
  const user = useMemo(() => {
    if (mockUser !== undefined) {
      return mockUser;
    }
    return parseJwtFromCookie();
  }, [mockUser]);

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      hasRole: (role: CrmRole) => user?.roles.includes(role) ?? false,
      hasAnyRole: (roles: CrmRole[]) => roles.some((r) => user?.roles.includes(r) ?? false),
    }),
    [user],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

// ---- Hook ----

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
